import { jsx as _jsx } from "react/jsx-runtime";
import { LegacyDevice } from 'console/device/device';
import { Capability } from 'console/device/device-capability';
import { DevicePermission } from 'console/device/device_ts';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { getDeviceSpecV2 } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { getDefaultDeviceUiSettings } from 'console/shared/content/ui-settings';
import { memo, useContext, useMemo } from 'react';
import { DeviceConfig } from './types';
import { isClimatic, isLegacyDevice, isOnlyGuardDevice, isSX, isVehicleDevice, } from 'console/shared/contexts/device/config/utils';
export const DeviceConfigProvider = memo(function DeviceConfigProvider(props) {
    const { device_id, config, ui_settings, device_type, children, name, temp_step, hardware_type, capabilities, user_id, thermostat_enable_guard, access, timezone, online, tab_key, } = props;
    const account = useAppSelector((state) => state.account);
    const { id: account_id, is_guest } = account;
    const has_guard_control = useMemo(() => LegacyDevice.has_guard(capabilities, !!thermostat_enable_guard) &&
        !LegacyDevice.has_capability(capabilities, Capability.has_car_controls) &&
        !LegacyDevice.has_capability(capabilities, Capability.has_z3k_settings), [capabilities, thermostat_enable_guard]);
    const has_siren = useMemo(() => LegacyDevice.has_capability(capabilities, Capability.has_siren_control) &&
        !LegacyDevice.has_capability(capabilities, Capability.has_car_controls), [capabilities]);
    const can_control_device = useMemo(() => online &&
        LegacyDevice.has_access(account_id, { timezone, user_id, access }, DevicePermission.control) &&
        !is_guest, [account_id, user_id, access, timezone, is_guest, online]);
    const { is_climatic, is_legacy_device, is_vehicle_device, is_sx, is_only_guard_device, } = useMemo(() => {
        if (!device_type) {
            return {
                is_climatic: false,
                is_legacy_device: false,
                is_only_guard_device: false,
                is_vehicle_device: false,
                is_sx: false,
            };
        }
        return {
            is_climatic: isClimatic(device_type),
            is_legacy_device: isLegacyDevice(device_type),
            is_sx: isSX(device_type),
            is_only_guard_device: isOnlyGuardDevice(device_type),
            is_vehicle_device: isVehicleDevice(device_type),
        };
    }, [device_type?.code]);
    const z3k_config = useMemo(() => config ?? {}, [config]);
    const device_ui_settings = useMemo(() => (ui_settings ? ui_settings : getDefaultDeviceUiSettings(z3k_config)), [ui_settings, z3k_config]);
    const device_spec = useMemo(() => getDeviceSpecV2(device_type?.code, hardware_type?.code), [device_type, hardware_type]);
    const support_service_mode = useMemo(() => (device_spec != null ? device_spec.has_service_mode : false) ||
        is_legacy_device, [device_spec, is_legacy_device]);
    const device_config_context = useMemo(() => ({
        device_id,
        name,
        z3k_config,
        user_id,
        account_id,
        device_ui_settings,
        can_control_device,
        support_service_mode,
        is_climatic,
        is_legacy_device,
        is_only_guard_device,
        is_sx,
        is_vehicle_device,
        is_guest,
        temp_step,
        hardware_type,
        capabilities,
        device_type,
        device_spec,
        tab_key,
        has_guard_control: has_guard_control || has_siren,
    }), [
        device_id,
        name,
        z3k_config,
        user_id,
        account_id,
        device_ui_settings,
        can_control_device,
        support_service_mode,
        is_climatic,
        is_legacy_device,
        is_only_guard_device,
        is_sx,
        is_vehicle_device,
        is_guest,
        temp_step,
        hardware_type,
        capabilities,
        device_type,
        device_spec,
        has_guard_control,
        has_siren,
        tab_key,
    ]);
    return (_jsx(DeviceConfig.Provider, { value: device_config_context, children: children }));
});
export const useDeviceConfig = () => useContext(DeviceConfig);
